import { useState, useEffect } from 'react';
import { X } from '@/components/icons';
import { SearchInput } from './ui/search-input';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { useAirportSearch } from '../lib/hooks';
import type { Airport } from '../lib/hooks';
import { ListSkeleton } from './ui/skeleton-presets';
import { ensureAirportDb } from '@/lib/airport-db';

export type { Airport };

interface AirportPickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (icaoCode: string) => void;
  title?: string;
  mode?: 'select' | 'view'; // 'select' for interactive mode, 'view' for read-only
  airportName?: string; // Used in view mode to display airport name
  searchParam?: string; // IATA code to pre-populate search when drawer opens
}

export function AirportPickerDrawer({
  isOpen,
  onClose,
  onSelect,
  title = 'Select Airport',
  searchParam,
}: AirportPickerDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [recentAirports, setRecentAirports] = useState<Airport[]>([]);

  // Ensure the SQLite airport DB is loaded on mount
  useEffect(() => {
    ensureAirportDb();
  }, []);

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load recent airports from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('recentAirports');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentAirports(parsed);
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Search airports from local SQLite database
  const { results: searchResults, isLoading: isSearching } = useAirportSearch(
    debouncedSearch.length >= 2 ? debouncedSearch : ''
  );

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  // Initialize search query when drawer opens
  useEffect(() => {
    if (isOpen) {
      if (searchParam) setSearchQuery(searchParam);
    }
  }, [isOpen, searchParam]);

  const handleSelect = (airport: Airport) => {
    if (!airport.icaoCode) return;

    // Save to recent airports
    const updated = [airport, ...recentAirports.filter(a => a.icaoCode !== airport.icaoCode)].slice(0, 10);
    setRecentAirports(updated);
    localStorage.setItem('recentAirports', JSON.stringify(updated));

    onSelect(airport.icaoCode);
    setSearchQuery('');
    onClose();
  };

  // Show recent airports or search results
  const displayedAirports = debouncedSearch.length >= 2 ? searchResults : recentAirports;

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="max-h-[85dvh] flex flex-col bg-surface-L0 shadow-lg">
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {title}
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {/* Search */}
        <div className="px-4 pb-2 shrink-0">
          <SearchInput
            placeholder="Search airport, city, or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
        </div>

        {/* List - Scrollable */}
        <div className="h-[400px] overflow-y-auto overscroll-contain px-4 py-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {debouncedSearch.length >= 2 && isSearching ? (
            <ListSkeleton />
          ) : displayedAirports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <p className="text-content-primary-subtle text-sm text-center">
                {debouncedSearch.length >= 2 ? 'No airports found' : 'Start typing to search airports'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {displayedAirports.map((airport, index) => (
                <div key={airport.icaoCode}>
                  <button
                    onClick={() => handleSelect(airport)}
                    className="w-full flex items-center gap-3 min-h-9 px-3 py-[7.5px] rounded-md transition-colors hover:bg-surface-L2/50"
                  >
                    <div className="flex-1 flex flex-col items-start justify-center text-left">
                      <p className="text-sm text-content-primary-bold leading-normal tracking-[0.07px]">
                        {airport.icaoCode || '—'}
                      </p>
                      <p className="text-xs text-content-primary-subtle leading-normal tracking-[0.18px]">
                        {airport.name}
                        {airport.regionName && `, ${airport.regionName}`}
                        {airport.countryName && `, ${airport.countryName}`}
                      </p>
                    </div>
                  </button>
                  {index < displayedAirports.length - 1 && (
                    <div className="h-px bg-border-L1" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
